import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import {
  BellRing, Plus, Edit, Trash2, CheckCircle2, Clock, CalendarDays, Repeat,
  RefreshCw, MessageCircle, Circle, User, Power
} from 'lucide-react';
import PageHeader from '../ui-pro/PageHeader';
import StatCard from '../ui-pro/StatCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKDAYS = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
const SHORT_DAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const trDate = (d) => (d ? String(d).slice(0, 10).split('-').reverse().join('.') : '-');
const todayISO = () => new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10);
const EMPTY = { title: '', type: 'once', date: todayISO(), weekday: 0, time: '09:00', notes: '', is_active: true, personnel_id: '' };

const RemindersTab = ({ personnelList = [] }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterPid, setFilterPid] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reminders`, { params: { personnel_id: filterPid } });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Hatırlatmalar yüklenemedi');
    } finally { setLoading(false); }
  }, [filterPid]);

  useEffect(() => { load(); }, [load]);

  const isAdmin = !!data?.is_admin;
  const rows = data?.reminders || [];
  const stats = data?.stats || {};

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, personnel_id: isAdmin ? (personnelList[0]?.id || '') : '' }); setShowForm(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      title: r.title, type: r.type, date: r.date || todayISO(),
      weekday: r.weekday ?? 0, time: r.time, notes: r.notes || '',
      is_active: r.is_active !== false, personnel_id: r.personnel_id,
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Başlık zorunlu'); return; }
    try {
      const body = { ...form, weekday: Number(form.weekday) };
      if (!isAdmin) delete body.personnel_id;
      if (editing) await axios.put(`${API}/reminders/${editing.id}`, body);
      else await axios.post(`${API}/reminders`, body);
      toast.success(editing ? 'Hatırlatma güncellendi' : 'Hatırlatma oluşturuldu — zamanı gelince WhatsApp mesajı gönderilecek');
      setShowForm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Kaydedilemedi'); }
  };

  const toggleDone = async (r) => {
    try { await axios.post(`${API}/reminders/${r.id}/toggle-done`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'İşaretlenemedi'); }
  };

  const toggleActive = async (r) => {
    try {
      await axios.put(`${API}/reminders/${r.id}`, {
        title: r.title, type: r.type, date: r.date, weekday: r.weekday,
        time: r.time, notes: r.notes, is_active: !r.is_active,
      });
      toast.success(!r.is_active ? 'Hatırlatma aktifleştirildi' : 'Hatırlatma duraklatıldı');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Güncellenemedi'); }
  };

  const remove = async (r) => {
    if (!window.confirm(`"${r.title}" hatırlatması silinsin mi?`)) return;
    try { await axios.delete(`${API}/reminders/${r.id}`); toast.success('Silindi'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Silinemedi'); }
  };

  const groups = useMemo(() => {
    const today = data?.today;
    const due = rows.filter(r => r.is_active && !r.done && (
      (r.type === 'once' && r.date === today)
      || (r.type === 'weekly' && r.weekday === (new Date(`${today}T00:00:00`).getDay() + 6) % 7)));
    const upcoming = rows.filter(r => !due.includes(r) && r.is_active && !r.done);
    const past = rows.filter(r => r.done || !r.is_active);
    return { due, upcoming, past };
  }, [rows, data?.today]);

  const Row = ({ r, tone }) => (
    <div className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
      tone === 'due' ? 'border-amber-200 bg-amber-50'
        : r.done ? 'border-[#E2E8F0] bg-[#F8FAFC] opacity-75'
          : !r.is_active ? 'border-[#E2E8F0] bg-slate-50 opacity-70' : 'border-[#E2E8F0] bg-white'
    }`} data-testid={`reminder-row-${r.id}`}>
      <button onClick={() => toggleDone(r)} className="mt-0.5 shrink-0" data-testid={`reminder-done-${r.id}`}>
        {r.done
          ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          : <Circle className="w-5 h-5 text-slate-300 hover:text-[#176B91]" />}
      </button>
      <div className="min-w-0 flex-1">
        <p className={`text-[13px] font-semibold text-slate-800 ${r.done ? 'line-through' : ''}`}>{r.title}</p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5">
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
            <Clock className="w-3 h-3" />{r.time}
          </span>
          {r.type === 'once'
            ? <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                <CalendarDays className="w-3 h-3" />{trDate(r.date)}
              </span>
            : <span className="inline-flex items-center gap-1 text-[11px] text-[#176B91] font-medium">
                <Repeat className="w-3 h-3" />Her {WEEKDAYS[r.weekday ?? 0]}
              </span>}
          {isAdmin && (
            <span className="inline-flex items-center gap-1 text-[11px] text-slate-600">
              <User className="w-3 h-3" />{r.personnel_name}
            </span>
          )}
          {!r.is_active && <Badge className="text-[10px] bg-slate-200 text-slate-600 hover:bg-slate-200">Duraklatıldı</Badge>}
          {r.last_sent_date && (
            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
              <MessageCircle className="w-3 h-3" />{trDate(r.last_sent_date)} gönderildi
            </span>
          )}
        </div>
        {r.notes && <p className="text-[11px] text-slate-500 mt-1">{r.notes}</p>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-500 hover:text-[#176B91]"
          onClick={() => toggleActive(r)} data-testid={`reminder-toggle-${r.id}`}>
          <Power className="w-3.5 h-3.5" />
        </Button>
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-[#176B91] hover:bg-sky-50"
          onClick={() => openEdit(r)} data-testid={`reminder-edit-${r.id}`}>
          <Edit className="w-3.5 h-3.5" />
        </Button>
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50"
          onClick={() => remove(r)} data-testid={`reminder-delete-${r.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );

  const Section = ({ title, items, tone, testid }) => (
    <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2.5">
        <p className="text-sm font-bold text-slate-900">{title}</p>
        <Badge className="text-[10px] bg-slate-100 text-slate-600 hover:bg-slate-100">{items.length}</Badge>
      </div>
      <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
        {items.length === 0
          ? <p className="text-[12px] text-slate-400 py-6 text-center">Kayıt yok</p>
          : items.map(r => <Row key={r.id} r={r} tone={tone} />)}
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="reminders-tab">
      <PageHeader
        icon={BellRing}
        title="Hatırlatmalar"
        description={isAdmin
          ? 'Tüm personelin kendisi için oluşturduğu görev hatırlatmaları — zamanı gelince WhatsApp ile gönderilir'
          : 'Kendi görev hatırlatmalarınız — zamanı gelince WhatsApp numaranıza mesaj gönderilir'}
        testid="reminders-header"
        actions={
          <>
            {isAdmin && (
              <Select value={filterPid} onValueChange={setFilterPid}>
                <SelectTrigger className="h-9 w-[200px]" data-testid="reminders-personnel-filter">
                  <SelectValue placeholder="Tüm personel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Personel</SelectItem>
                  {personnelList.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Button variant="outline" size="sm" className="h-9" onClick={load} data-testid="reminders-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" className="h-9 bg-[#0B172A] hover:bg-[#13233C]" onClick={openNew} data-testid="reminder-new-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Yeni Hatırlatma
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={BellRing} tone="amber" testid="reminder-kpi-today" label="Bugün" value={stats.today ?? 0} sub="bugünkü hatırlatma" />
        <StatCard icon={CheckCircle2} tone="emerald" testid="reminder-kpi-active" label="Aktif" value={stats.active ?? 0} sub="takipte olan" />
        <StatCard icon={Repeat} tone="indigo" testid="reminder-kpi-weekly" label="Haftalık Tekrar" value={stats.weekly ?? 0} sub="her hafta aynı gün" />
        <StatCard icon={CalendarDays} tone="sky" testid="reminder-kpi-once" label="Tek Seferlik" value={stats.once_pending ?? 0} sub="bekleyen görev" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        <Section title="Bugün" items={groups.due} tone="due" testid="reminders-due" />
        <Section title="Planlanan" items={groups.upcoming} testid="reminders-upcoming" />
        <Section title="Tamamlanan / Duraklatılan" items={groups.past} testid="reminders-past" />
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg" data-testid="reminder-modal">
          <DialogHeader>
            <DialogTitle>{editing ? 'Hatırlatmayı Düzenle' : 'Yeni Hatırlatma'}</DialogTitle>
            <DialogDescription>
              Belirttiğiniz gün ve saatte WhatsApp numaranıza "şu görevin var" mesajı gönderilir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {isAdmin && !editing && (
              <div>
                <Label className="text-[12px]">Personel</Label>
                <Select value={form.personnel_id} onValueChange={(v) => setForm(f => ({ ...f, personnel_id: v }))}>
                  <SelectTrigger data-testid="reminder-form-personnel"><SelectValue placeholder="Personel seçin" /></SelectTrigger>
                  <SelectContent>
                    {personnelList.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-[12px]">Ne hatırlatılsın? *</Label>
              <Input value={form.title} placeholder="Örn: Kasa sayımını yap"
                onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                data-testid="reminder-form-title" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setForm(f => ({ ...f, type: 'once' }))}
                className={`rounded-lg border px-3 py-2 text-left transition-colors ${form.type === 'once' ? 'border-[#176B91] bg-sky-50' : 'border-[#E2E8F0] hover:bg-slate-50'}`}
                data-testid="reminder-form-type-once">
                <p className="text-[13px] font-semibold text-slate-800 flex items-center gap-1.5">
                  <CalendarDays className="w-3.5 h-3.5" /> Tek Seferlik
                </p>
                <p className="text-[11px] text-slate-500">Belirli bir tarih ve saat</p>
              </button>
              <button type="button" onClick={() => setForm(f => ({ ...f, type: 'weekly' }))}
                className={`rounded-lg border px-3 py-2 text-left transition-colors ${form.type === 'weekly' ? 'border-[#176B91] bg-sky-50' : 'border-[#E2E8F0] hover:bg-slate-50'}`}
                data-testid="reminder-form-type-weekly">
                <p className="text-[13px] font-semibold text-slate-800 flex items-center gap-1.5">
                  <Repeat className="w-3.5 h-3.5" /> Haftalık Tekrar
                </p>
                <p className="text-[11px] text-slate-500">Her hafta aynı gün ve saat</p>
              </button>
            </div>
            {form.type === 'once' ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[12px]">Tarih *</Label>
                  <Input type="date" value={form.date}
                    onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
                    data-testid="reminder-form-date" />
                </div>
                <div>
                  <Label className="text-[12px]">Saat *</Label>
                  <Input type="time" value={form.time}
                    onChange={(e) => setForm(f => ({ ...f, time: e.target.value }))}
                    data-testid="reminder-form-time" />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div>
                  <Label className="text-[12px]">Hangi gün? *</Label>
                  <div className="grid grid-cols-7 gap-1 mt-1">
                    {SHORT_DAYS.map((d, i) => (
                      <button key={d} type="button" onClick={() => setForm(f => ({ ...f, weekday: i }))}
                        className={`h-9 rounded-md text-[12px] font-semibold transition-colors ${Number(form.weekday) === i ? 'bg-[#0B172A] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                        data-testid={`reminder-form-weekday-${i}`}>{d}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-[12px]">Saat *</Label>
                  <Input type="time" value={form.time}
                    onChange={(e) => setForm(f => ({ ...f, time: e.target.value }))}
                    data-testid="reminder-form-time-weekly" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-[12px]">Not (opsiyonel)</Label>
              <Textarea rows={2} value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Mesaja eklenecek ek detay" data-testid="reminder-form-notes" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setShowForm(false)} data-testid="reminder-form-cancel">İptal</Button>
              <Button className="bg-[#0B172A] hover:bg-[#13233C]" onClick={save} data-testid="reminder-form-save">
                {editing ? 'Güncelle' : 'Oluştur'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RemindersTab;
